import { Component, computed, inject, signal, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BuildingEquipmentService, BuildingItemDto, BuildingService } from '@artemis-software/wr-api';
import { firstValueFrom } from 'rxjs';
import { PendingChanges } from '@guards/pending-changes.guard';
import {
  BuildingAndSection,
  BuildingEquipmentDetailFormComponent,
} from './building-equipment-detail-form/building-equipment-detail-form.component';
import { BuildingEquipmentDetailDto } from '@artemis-software/wr-api/model/buildingEquipmentDetailDto';
import { CurrentUserService } from '@services/current-user.service';
import { NbTabComponent, NbToastrService } from '@nebular/theme';
import {
  BuildingEquipmentFieldsTabComponent,
} from './building-equipment-fields-tab/building-equipment-fields-tab.component';
import {
  BuildingEquipmentFoldersTabComponent,
} from './building-equipment-folders-tab/building-equipment-folders-tab.component';


@Component({
  standalone: false,
  selector: 'wr-building-tga-type-detail-page',
  templateUrl: './building-equipment-detail-page.component.html',
  styleUrls: ['./building-equipment-detail-page.component.scss'],
})
export class BuildingEquipmentDetailPageComponent implements PendingChanges {

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly buildingEquipmentService = inject(BuildingEquipmentService);
  private readonly buildingService = inject(BuildingService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly toastrService = inject(NbToastrService);

  @ViewChild(BuildingEquipmentDetailFormComponent)
  buildingEquipmentDetailFormComponent?: BuildingEquipmentDetailFormComponent;

  @ViewChild(BuildingEquipmentFieldsTabComponent)
  fieldsTabComponent?: BuildingEquipmentFieldsTabComponent;

  @ViewChild(BuildingEquipmentFoldersTabComponent)
  foldersTabComponent?: BuildingEquipmentFoldersTabComponent;

  buildingEquipment: BuildingEquipmentDetailDto | null | undefined = undefined;

  readonly buildingEquipmentId = signal<string | null>(null);

  /** Preselected building for a new TGA, taken from the `buildingId` query param. */
  readonly buildingAndSection = signal<BuildingAndSection | undefined>(undefined);

  readonly isNew = computed(() => this.buildingEquipmentId() === 'new');
  readonly canEdit = signal(false);
  readonly isEditing = signal(false);
  readonly isSaving = signal(false);

  readonly buildingEquipmentReady = () => this.buildingEquipment !== undefined;
  readonly viewMaintenanceTimers = computed(
    () => this.currentUserService.hasPrivilege('viewInternalMaintenanceTimers')
      || this.currentUserService.hasPrivilege('viewExternalMaintenanceTimers'),
  );
  readonly canViewFolderContents = computed(
    () => this.currentUserService.hasPrivilege('viewAttachmentFolderContents'),
  );

  private readonly activeTabTitle = signal('Grunddaten');

  /** Guards the preselection against out-of-order answers when `buildingId` changes rapidly. */
  private preselectRequestId = 0;

  constructor() {
    this.route.paramMap.subscribe(async (params) => {
      const id = params.get('id');
      this.buildingEquipmentId.set(id);
      if (id) {
        if (id === 'new') {
          this.buildingEquipment = null;
          this.canEdit.set(true);
          this.isEditing.set(true);
        } else {
          this.buildingEquipmentService.findById(id).subscribe((dto) => {
            this.buildingEquipment = dto;
            this.canEdit.set(dto.permissions.canEdit);
          });
        }
      } else {
        await this.router.navigate(['buildingEquipments'], { replaceUrl: true });
      }
    });

    // Own subscription: a pure query-param change does not re-emit paramMap, so reading the
    // snapshot above would keep the first buildingId forever.
    this.route.queryParamMap.subscribe((params) => {
      // Not `buildingEquipmentId()`: `advanceActivatedRoute` emits queryParams *before* params, so
      // when both change at once the signal still holds the previous id. `route.snapshot` is
      // assigned before either emission and is therefore already current here.
      if (this.route.snapshot.paramMap.get('id') === 'new') {
        void this.loadPreselectedBuilding(params.get('buildingId'));
      }
    });
  }

  private async loadPreselectedBuilding(buildingId: string | null): Promise<void> {
    // Incremented before the early return on purpose: switching to a URL without `buildingId` must
    // also invalidate an in-flight request, otherwise its late answer would still preselect.
    const requestId = ++this.preselectRequestId;
    if (!buildingId) {
      return;
    }
    const building = await this.findBuilding(buildingId);
    if (requestId !== this.preselectRequestId) {
      // A newer buildingId superseded this request while it was in flight.
      return;
    }
    if (building) {
      this.buildingAndSection.set({ building });
    } else {
      this.toastrService.warning(
        'Das Gebäude konnte nicht vorausgewählt werden. Bitte manuell auswählen.',
        'TGA erstellen',
      );
    }
  }

  /**
   * For internal users this runs through plain `repository.findAllById` — no `deleted = false`,
   * no ViewBuildingPolicy. Only the external-tenant path is really scoped. A missing result is
   * therefore not proof that the building is inaccessible, just that we cannot preselect it.
   */
  private async findBuilding(buildingId: string): Promise<BuildingItemDto | undefined> {
    try {
      const buildings = await firstValueFrom(this.buildingService.findByIds([buildingId]));
      return buildings[0];
    } catch {
      return undefined;
    }
  }

  onTabChange(tab: NbTabComponent): void {
    this.activeTabTitle.set(tab.tabTitle ?? 'Grunddaten');
  }

  onEdit(): void {
    this.isEditing.set(true);
    this.buildingEquipmentDetailFormComponent?.enable();
    this.fieldsTabComponent?.onEdit();
  }

  async submit(): Promise<void> {
    // Re-entry guard: `isEditing` only flips after the await below, so without this a double click
    // would fire two `merge` calls — and while creating, both carry `id: null` and create two TGAs.
    if (this.isSaving()) {
      return;
    }
    this.isSaving.set(true);
    try {
      switch (this.activeTabTitle()) {
        case 'Grunddaten': {
          // A failed client-side validation must not end edit mode: on `new` there is no edit
          // button to get back in, so both footer buttons would stay disabled for good.
          const saved = (await this.buildingEquipmentDetailFormComponent?.submit()) ?? true;
          if (!saved) {
            return;
          }
          break;
        }
        case 'Felder': await this.fieldsTabComponent?.submit(); break;
        case 'Dokumente': {
          const saved = (await this.foldersTabComponent?.submit()) ?? true;
          if (!saved) {
            return;
          }
          break;
        }
      }
      this.isEditing.set(false);
    } finally {
      this.isSaving.set(false);
    }
  }

  async cancel(): Promise<void> {
    this.isEditing.set(false);
    await this.fieldsTabComponent?.cancel();
    this.foldersTabComponent?.cancel();
    // Reload the entity to reset the form
    const buildingEquipmentId = this.buildingEquipmentId();
    if (buildingEquipmentId && buildingEquipmentId !== 'new') {
      this.buildingEquipmentService.findById(buildingEquipmentId).subscribe(dto => {
        this.buildingEquipment = dto;
      });
    }
  }

  onSubmitSuccess(buildingEquipment: BuildingEquipmentDetailDto): void {
    this.isEditing.set(false);
    this.router.navigate(['buildingEquipment', buildingEquipment.id], { replaceUrl: true });
  }

  onDeleteSuccess(): void {
    this.navigateBack();
  }

  hasPendingChanges(): boolean | Promise<boolean> {
    return (this.buildingEquipmentDetailFormComponent?.hasPendingChanges() ?? false)
      || (this.fieldsTabComponent?.hasPendingChanges() ?? false)
      || (this.foldersTabComponent?.hasPendingChanges() ?? false);
  }

  async navigateBack(): Promise<void> {
    await window.history.back();
  }
}
